import React from "react";
import { 
    FaHouseUser, 
    FaChair, 
    FaToilet, 
    FaRegClipboard, 
    FaBroom, 
    FaSprayCan, 
    FaHandSparkles, 
    FaCheckCircle, 
    FaArrowRight, 
    FaPhoneAlt,
    FaShieldAlt,
    FaEnvelope,
    FaStar,
    FaTools
} from "react-icons/fa"; 
import cleaningImage from '../../../Asset/entretiens.jpg'; 
import EntretiensImg from '../../../Asset/Entretien_Logement.jpg';
import { Link } from 'react-router-dom'; 
import { Helmet } from 'react-helmet';
import { useColorMode, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MénageDomicile = () => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const sectionVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const formules = [
        {
            title: "Régulier",
            subtitle: "Hebdomadaire",
            icon: FaBroom,
            desc: "Nettoyage complet et régulier de l'ensemble de votre logement pour une hygiène constante.",
            list: ["Souplesse totale", "Hygiène optimale", "Pièces à vivre"]
        },
        {
            title: "Spécifique",
            subtitle: "Organisation",
            icon: FaRegClipboard,
            desc: "Missions spéciales : rangement méthodique, classement de documents et réorganisation d'espaces.",
            list: ["Classement", "Rangement", "Optimisation"]
        },
        {
            title: "Lourd",
            subtitle: "Profondeur",
            icon: FaSprayCan,
            desc: "Remise au propre totale nécessitant du matériel et des produits professionnels (min. 10h).",
            list: ["Matériel pro", "Haute performance", "Remise à neuf"]
        },
        {
            title: "Occasionnel",
            subtitle: "Ponctuel",
            icon: FaHandSparkles,
            desc: "Interventions à la commande, sans aucun engagement, idéal pour les grands nettoyages de saison.",
            list: ["Sans engagement", "Sur commande", "Grand nettoyage"]
        }
    ];

    const areas = [
        { icon: FaHouseUser, title: "Nettoyage de votre maison" },
        { icon: FaChair, title: "Nettoyage de votre mobilier" },
        { icon: FaToilet, title: "Nettoyage de vos sanitaires" },
        { icon: FaRegClipboard, title: "Ménage de votre appartement" },
    ];

    return (
        <Box position="relative" overflow="hidden" className={isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}>
            <Helmet>
                <title>Entretien du domicile — Hetep Iaout Services</title>
                <meta name="description" content="Entretien du domicile par des professionnels certifiés en Île-de-France. Ménage, repassage, courses. Services éligibles au crédit d'impôt de 50 %. Devis gratuit." />
                <link rel="canonical" href="https://www.hisvie.com/entretien-du-domicile/" />
            </Helmet>
            
            <div className="w-full">
                
                {/* --- HERO SECTION (STYLE SENIOR) --- */}
                <div className="relative w-full min-h-[100vh] flex items-center pt-20 overflow-hidden">
                    <motion.img 
                        initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 10, ease: "easeOut" }}
                        src={EntretiensImg} alt="Entretien du domicile" 
                        className="absolute inset-0 w-full h-full object-cover z-0 origin-center" 
                    />
                    <div className="absolute inset-0 bg-black/60 md:bg-black/0 md:bg-gradient-to-r md:from-[#0a0f16]/90 md:via-[#0a0f16]/60 md:to-black/40 z-10"></div>

                    <div className="relative z-20 w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row items-center justify-between">
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
                            className="w-full lg:w-2/3"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-8 shadow-lg">
                                <FaShieldAlt className="text-blue-400 text-sm" />
                                <span className="text-white text-xs font-bold tracking-widest uppercase">Assistance & Propreté</span>
                            </div>

                            <h1 className="text-4xl md:text-7xl font-black leading-[1.1] tracking-tight mb-6 text-white">
                                L'entretien de votre domicile, <br />
                                <span className="text-blue-400 font-light italic font-serif">en toute sérénité.</span>
                            </h1>

                            <p className="text-lg md:text-xl mb-10 max-w-lg leading-relaxed text-gray-300 font-light">
                                Une assistance personnalisée pour assurer l'hygiène de votre logement et préserver un cadre de vie sain.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/demande-de-devis">
                                    <motion.button whileHover={{ scale: 1.02 }} className="w-full sm:w-auto bg-blue-600 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-3">
                                        Obtenir un devis <FaArrowRight size={14} />
                                    </motion.button>
                                </Link>
                                <Link to="/contact">
                                    <motion.button whileHover={{ scale: 1.02 }} className="w-full sm:w-auto font-bold py-4 px-8 rounded-2xl border bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-md transition-all">
                                        Parler à un conseiller
                                    </motion.button>
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div 
                            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.6 }}
                            className="hidden lg:flex w-1/3 flex-col items-end"
                        >
                            <div className="p-6 rounded-[2rem] shadow-2xl border backdrop-blur-xl bg-white/10 border-white/20">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-white shrink-0 shadow-inner">
                                        <FaStar size={20} />
                                    </div>
                                    <div>
                                        <p className="font-black text-lg mb-1 text-white">Cap'Handéo</p>
                                        <p className="text-xs leading-relaxed text-gray-200">Certification Excellence pour l'accompagnement à domicile.</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* --- FORMULES DE MÉNAGE (GRILLE POUR ÉVITER LES IMAGES RÉPÉTÉES) --- */}
                <section className="py-24 px-6 max-w-7xl mx-auto">
                    <motion.div 
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
                        className="text-center mb-20"
                    >
                        <h2 className={`text-4xl md:text-6xl font-black mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                            Nos formules de <span className="text-blue-600 font-serif italic font-light">ménage.</span>
                        </h2>
                        <div className="w-16 h-1 bg-blue-600 mx-auto rounded-full"></div>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {formules.map((formule, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                                className={`p-8 rounded-[2.5rem] border transition-all duration-300 flex flex-col h-full ${isDark ? 'bg-gray-900 border-gray-800 hover:border-blue-500' : 'bg-white border-gray-100 shadow-xl hover:shadow-2xl hover:border-blue-200'}`}
                            >
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                    <formule.icon size={28} />
                                </div>
                                <span className="text-blue-600 font-bold text-xs uppercase tracking-widest mb-2">{formule.subtitle}</span>
                                <h3 className={`text-2xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{formule.title}</h3>
                                <p className={`text-sm leading-relaxed mb-8 flex-grow font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{formule.desc}</p>
                                <ul className="space-y-3 pt-6 border-t border-gray-100 dark:border-gray-800">
                                    {formule.list.map((item, i) => (
                                        <li key={i} className="flex items-center gap-3">
                                            <FaCheckCircle className="text-blue-600 text-[10px]" />
                                            <span className={`text-xs font-bold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* --- SECTION EXPERTISE UNIQUE (FOCUS IMAGE) --- */}
                <motion.section 
                    initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
                    className="py-24 bg-blue-600/5"
                >
                    <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-16">
                        <div className="flex-1 relative">
                            <div className="absolute -inset-4 bg-blue-600/10 rounded-[3rem] -rotate-2"></div>
                            <img 
                                src={cleaningImage} 
                                alt="Expertise Ménage" 
                                className="relative z-10 rounded-[2.5rem] shadow-2xl w-full h-[450px] object-cover"
                            />
                        </div>
                        <div className="flex-1 space-y-8">
                            <h2 className={`text-3xl md:text-5xl font-black leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                Une rigueur professionnelle <br />
                                <span className="text-blue-600 font-serif italic font-light">pour chaque recoin.</span>
                            </h2>
                            <p className={`text-lg leading-relaxed font-light ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                Nos intervenants sont formés aux protocoles d'hygiène les plus stricts. Qu'il s'agisse d'un entretien hebdomadaire ou d'une remise au propre lourde, nous utilisons des méthodes validées pour garantir la longévité de votre mobilier et la santé de votre foyer.
                            </p>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <FaTools className="text-blue-600 text-2xl" />
                                    <h4 className={`font-bold ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Matériel Adapté</h4>
                                    <p className="text-sm text-gray-500">Utilisation de produits éco-responsables et performants.</p>
                                </div>
                                <div className="space-y-2">
                                    <FaShieldAlt className="text-blue-600 text-2xl" />
                                    <h4 className={`font-bold ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Confiance Totale</h4>
                                    <p className="text-sm text-gray-500">Personnel rigoureusement sélectionné et assuré.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* --- BENTO SURFACES --- */}
                <motion.section 
                    className="max-w-6xl mx-auto px-6 py-24 md:py-32"
                    variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}
                >
                    <div className="text-center mb-16">
                        <h2 className={`text-2xl md:text-4xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Des interventions sur toutes vos surfaces</h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                        {areas.map((area, index) => (
                            <div key={index} className={`flex flex-col items-center justify-center text-center p-8 rounded-[2.5rem] border transition-all duration-300 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-lg hover:shadow-2xl hover:-translate-y-1'}`}>
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                    <area.icon size={24} />
                                </div>
                                <h4 className={`text-lg font-bold leading-snug ${isDark ? 'text-gray-200' : 'text-[#1B365D]'}`}>{area.title}</h4>
                            </div>
                        ))}
                    </div>
                </motion.section>

                {/* --- CTA FINAL HARMONISÉ --- */}
                <motion.section 
                    initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
                    className="max-w-4xl mx-auto px-4 pb-24"
                >
                    <div className={`relative overflow-hidden rounded-[2.5rem] p-8 md:p-14 flex flex-col md:flex-row items-center gap-10 border shadow-2xl ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                        <div className="flex-1 z-10 text-center md:text-left">
                            <h2 className={`text-3xl md:text-5xl font-black mb-4 tracking-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                Déléguez l'entretien de votre <span className="text-blue-600 font-serif italic font-light">domicile</span>.
                            </h2>
                            <p className={`text-base md:text-lg max-w-md mx-auto md:mx-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Évaluons ensemble vos besoins pour un devis personnalisé sous 48h.</p>
                        </div>

                        <div className="flex flex-col sm:flex-row w-full md:w-auto gap-4 z-10 shrink-0">
                            <Link to="/contact" className="w-full sm:w-auto">
                                <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-blue-700 transition-all w-full shadow-lg shadow-blue-600/30">
                                    <FaPhoneAlt size={16} /> Nous contacter
                                </button>
                            </Link>
                            <a href="mailto:his@donnerlavieauxannees.com" className={`flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold transition-all border w-full sm:w-auto ${isDark ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100'}`}>
                                <FaEnvelope size={16} /> Par Email
                            </a>
                        </div>
                    </div>
                </motion.section>
            </div>
        </Box>
    );
};

export default MénageDomicile;